import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminWs, useAdminWsConnected } from '../context/AdminWsContext';
import { RotateCw, Trash2, ArrowDownToLine, Pause, Play, Terminal, AlertCircle, CheckCircle, X, Download, Search, Clock, Cpu } from 'lucide-react';

// Сколько строк держим в памяти вкладки. Сервер шлёт батчами по WS; история
// при открытии приходит из кольцевого буфера (GET /server/logs).
const MAX_LINES = 2000;

const LEVEL_COLORS = {
  error:  'var(--red)',
  warn:   '#eab308',
  info:   'var(--text-2)',
  log:    'var(--text-2)',
  marker: 'var(--brand)',
};

const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

function fmtUptime(sec) {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (d > 0) return `${d}д ${h}ч ${m}м`;
  if (h > 0) return `${h}ч ${m}м`;
  if (m > 0) return `${m}м ${s}с`;
  return `${s}с`;
}

const fmtMb = (bytes) => bytes == null ? '—' : `${(bytes / 1024 / 1024).toFixed(0)} МБ`;

export const ServerConsole = () => {
  const { fetchAuthorized } = useAuth();
  const wsConnected = useAdminWsConnected();

  const [lines, setLines]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [canRestart, setCanRestart]   = useState(false);
  const [restartReason, setRestartReason] = useState(null);
  const [serviceName, setServiceName] = useState(null);
  const [restarting, setRestarting]   = useState(false);
  const [autoScroll, setAutoScroll]   = useState(true);
  const [toast, setToast]             = useState(null);
  const [status, setStatus]           = useState(null); // { uptime_seconds, rss_bytes, ... }

  // Фильтры отображения (на сервер не влияют — фильтруем уже принятые строки).
  const [levelFilter, setLevelFilter] = useState('all'); // all | warn | error
  const [search, setSearch]           = useState('');

  const scrollRef        = useRef(null);
  const autoScrollRef    = useRef(true);
  autoScrollRef.current  = autoScroll;
  const awaitingRestart  = useRef(false); // мы инициировали рестарт — ждём переподключения
  const prevConnected    = useRef(wsConnected);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const appendLines = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setLines(prev => {
      const next = prev.concat(incoming);
      return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
    });
  }, []);

  // История из кольцевого буфера при открытии вкладки.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAuthorized('/api/dashboard/server/logs')
      .then(r => r.json())
      .then(d => {
        if (!alive || !d.success) return;
        setLines((d.lines || []).slice(-MAX_LINES));
        setCanRestart(!!d.can_restart);
        setRestartReason(d.restart_reason || null);
        setServiceName(d.service_name || null);
      })
      .catch(() => { if (alive) showToast('Не удалось загрузить логи', 'error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fetchAuthorized]);

  // Аптайм/память: опрос раз в 10с (и сразу при открытии).
  useEffect(() => {
    let alive = true;
    const load = () => fetchAuthorized('/api/dashboard/server/status')
      .then(r => r.json())
      .then(d => { if (alive && d.success) setStatus(d); })
      .catch(() => {});
    load();
    const t = setInterval(load, 10_000);
    return () => { alive = false; clearInterval(t); };
  }, [fetchAuthorized]);

  // Live-поток по WebSocket.
  useAdminWs(useCallback((msg) => {
    if (msg.event === 'server_log') appendLines(msg.data?.lines);
  }, [appendLines]));

  // Детект «сервер перезапустился»: после нашего рестарта WS отвалится и
  // переподключится — на переходе false→true рисуем разделитель.
  useEffect(() => {
    if (!prevConnected.current && wsConnected && awaitingRestart.current) {
      awaitingRestart.current = false;
      setRestarting(false);
      const ts = new Date().toLocaleTimeString('ru-RU');
      setLines(prev => prev.concat([{ level: 'marker', ts, text: '─────── сервер перезапущен ───────' }]));
      showToast('Сервер снова на связи', 'success');
    }
    prevConnected.current = wsConnected;
  }, [wsConnected]);

  // Видимые строки с учётом фильтра уровня и текстового поиска.
  const visibleLines = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lines.filter(l => {
      if (l.level === 'marker') return true; // маркеры показываем всегда
      if (levelFilter === 'error' && l.level !== 'error') return false;
      if (levelFilter === 'warn'  && l.level !== 'warn' && l.level !== 'error') return false;
      if (q && !l.text.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lines, levelFilter, search]);

  // Автоскролл вниз при новых строках (если включён).
  useEffect(() => {
    if (autoScrollRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleLines]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  const handleRestart = async () => {
    if (!window.confirm(
      `Перезапустить сервер${serviceName ? ` (служба «${serviceName}»)` : ''}?\n\n` +
      'Дашборд на несколько секунд потеряет связь и переподключится автоматически. ' +
      'Киоски в этот момент переживут разрыв и подтянут состояние по heartbeat.'
    )) return;

    setRestarting(true);
    awaitingRestart.current = true;
    try {
      const r = await fetchAuthorized('/api/dashboard/server/restart', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (d.success) {
        showToast('Команда на перезапуск отправлена. Ждём переподключения…', 'success');
      } else {
        showToast(d.error || 'Не удалось перезапустить', 'error');
        setRestarting(false);
        awaitingRestart.current = false;
      }
    } catch (e) {
      if (String(e.message) !== 'Unauthorized') showToast('Ошибка сети', 'error');
      setRestarting(false);
      awaitingRestart.current = false;
    }
  };

  // Выгрузка того, что сейчас на экране (с учётом фильтров).
  const download = () => {
    const text = visibleLines
      .map(l => l.level === 'marker' ? l.text : `${l.ts} ${l.level.toUpperCase().padEnd(5)} ${l.text}`)
      .join('\r\n');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `server-log_${stamp}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clear = () => setLines([]);

  const filterBtns = [
    { id: 'all',   label: 'Всё' },
    { id: 'warn',  label: '⚠ Предупреждения' },
    { id: 'error', label: '⛔ Ошибки' },
  ];

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-1)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Terminal size={20} /> Консоль сервера
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {serviceName ? <span>служба <code>{serviceName}</code></span> : <span>live-поток</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Аптайм процесса">
              <Clock size={12} /> {fmtUptime(status?.uptime_seconds)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Память процесса (RSS)">
              <Cpu size={12} /> {fmtMb(status?.rss_bytes)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: wsConnected ? 'var(--green)' : 'var(--red)' }}>
              ● {wsConnected ? 'online' : 'offline'}
            </span>
            <span>{visibleLines.length}/{lines.length} строк</span>
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => { setAutoScroll(a => !a); if (!autoScroll && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }}
            title={autoScroll ? 'Автоскролл включён' : 'Автоскролл выключен'}
            style={btnStyle(autoScroll)}
          >
            {autoScroll ? <Pause size={14} /> : <Play size={14} />}
            {autoScroll ? 'Автоскролл' : 'Пауза'}
          </button>
          <button onClick={download} title="Скачать видимый лог в .txt" style={btnStyle(false)}>
            <Download size={14} /> Скачать
          </button>
          <button onClick={clear} title="Очистить вид (на сервере логи остаются)" style={btnStyle(false)}>
            <Trash2 size={14} /> Очистить
          </button>
          <button
            onClick={handleRestart}
            disabled={!canRestart || restarting}
            title={canRestart ? 'Перезапустить службу сервера' : (restartReason || 'Перезапуск недоступен')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '8px',
              border: '1px solid var(--red)',
              background: (!canRestart || restarting) ? 'transparent' : 'rgba(239,68,68,0.12)',
              color: 'var(--red)', fontSize: '13px',
              cursor: (!canRestart || restarting) ? 'not-allowed' : 'pointer',
              opacity: (!canRestart || restarting) ? 0.5 : 1,
            }}
          >
            <RotateCw size={14} className={restarting ? 'spin' : ''} />
            {restarting ? 'Перезапуск…' : 'Перезапустить сервер'}
          </button>
        </div>
      </div>

      {/* Панель фильтров */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {filterBtns.map(f => (
            <button key={f.id} onClick={() => setLevelFilter(f.id)} style={btnStyle(levelFilter === f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: '360px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск по тексту…"
            style={{
              width: '100%', padding: '8px 30px 8px 32px', borderRadius: '8px',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-1)', fontSize: '13px',
            }}
          />
          {search && (
            <button className="btn-icon" onClick={() => setSearch('')} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', width: '22px', height: '22px', border: 'none' }}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {!canRestart && restartReason && (
        <p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AlertCircle size={13} style={{ color: '#eab308' }} /> Перезапуск с дашборда недоступен: {restartReason}
        </p>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="glass-card"
        style={{
          height: 'calc(100vh - 290px)', minHeight: '300px',
          overflowY: 'auto', padding: '12px 16px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: '12.5px', lineHeight: 1.55,
          background: 'rgba(0,0,0,0.30)',
        }}
      >
        {loading ? (
          <div style={{ color: 'var(--text-3)' }}>Загрузка логов…</div>
        ) : visibleLines.length === 0 ? (
          <div style={{ color: 'var(--text-3)' }}>
            {lines.length === 0 ? 'Пока пусто. Новые строки появятся здесь в реальном времени.' : 'Под фильтр ничего не подошло.'}
          </div>
        ) : (
          visibleLines.map((l, i) => l.level === 'marker' ? (
            <div key={i} style={{ color: 'var(--brand)', fontWeight: 700, textAlign: 'center', margin: '6px 0', opacity: 0.85 }}>
              {l.text} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{l.ts}</span>
            </div>
          ) : (
            <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: LEVEL_COLORS[l.level] || 'var(--text-2)' }}>
              <span style={{ color: 'var(--text-3)' }}>{l.ts}</span>{' '}
              {l.level === 'error' && <span style={{ color: 'var(--red)', fontWeight: 700 }}>ERR </span>}
              {l.level === 'warn'  && <span style={{ color: '#eab308', fontWeight: 700 }}>WRN </span>}
              {l.text}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

function btnStyle(active) {
  return {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 12px', borderRadius: '8px',
    border: '1px solid var(--border)',
    background: active ? 'rgba(99,102,241,0.12)' : 'transparent',
    color: active ? 'var(--brand)' : 'var(--text-2)',
    cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap',
  };
}
